import {
  type BulkOneClickPrintPickListPosition,
  PrintableDocument,
} from "@redotech/redo-fulfillment-group-common/print-job";
import { PrinterOptionsSchema } from "@redotech/redo-fulfillment-group-common/printer";
import type { Workstation } from "@redotech/redo-fulfillment-printing-common/workstation";
import type { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import { assertNever } from "@redotech/util/type";
import * as z from "zod";

export const PrintableBatchSchema = z.union([
  z.object({ batchId: z.string(), batchNumber: z.string() }),
  z.object({ fulfillmentGroupIds: z.array(z.string()) }),
]);
export type PrintableBatch = z.infer<typeof PrintableBatchSchema>;

export const PrintOptionsSchema = z.object({
  workstationId: z.string().optional(),
  printerId: z.number().optional(),
  printerOptions: PrinterOptionsSchema.optional(),
  templateId: z.string().optional(),
});
export type PrintOptions = z.infer<typeof PrintOptionsSchema>;

export function getParcelBoxLabelPrintOptions(
  workstation: Workstation | undefined,
  selectedPrinterId: number | undefined,
): PrintOptions | undefined {
  if (workstation) {
    return workstation.barcodePrinterId
      ? {
          printerId: workstation.barcodePrinterId,
          printerOptions: workstation.barcodePrinterOptions,
        }
      : undefined;
  }

  return selectedPrinterId ? { printerId: selectedPrinterId } : undefined;
}

export async function printParcelBoxLabelIfConfigured({
  client,
  input,
  workstation,
  selectedPrinterId,
  disabled,
}: {
  client: Pick<RedoOutboundLabelsRpcClient, "getParcelBoxLabel">;
  input: { fulfillmentGroupId: string; parcelIndex: number };
  workstation: Workstation | undefined;
  selectedPrinterId: number | undefined;
  disabled: boolean;
}): Promise<boolean> {
  if (disabled) {
    return false;
  }

  const printOptions = getParcelBoxLabelPrintOptions(
    workstation,
    selectedPrinterId,
  );
  if (!printOptions?.printerId) {
    return false;
  }

  await client.getParcelBoxLabel({ ...input, ...printOptions });
  return true;
}

type PrintBatchRpcClient = Pick<
  RedoOutboundLabelsRpcClient,
  | "printBillOfLading"
  | "printCommercialInvoices"
  | "printLabels"
  | "printLocalPickUpLabels"
  | "printOrderSummaryReport"
  | "printPackingSlip"
  | "printParcelPackingSlips"
  | "printPersonalizedSlip"
  | "printPickList"
  | "printReturnLabels"
>;

export function getBulkOneClickPrintPickListInput(
  bulkOneClickPrintPickListPosition?: BulkOneClickPrintPickListPosition,
) {
  return bulkOneClickPrintPickListPosition
    ? { bulkOneClickPrintPickListPosition }
    : {};
}

export async function doPrintBatch(
  client: PrintBatchRpcClient,
  batch: PrintableBatch,
  document: PrintableDocument,
  {
    workstationId,
    printerId,
    printerOptions,
    bulkOneClickPrintPickListPosition,
    templateId,
    signal,
  }: PrintOptions & {
    bulkOneClickPrintPickListPosition?: BulkOneClickPrintPickListPosition;
    signal?: AbortSignal;
  },
): Promise<{
  url: string;
  batchFailed?: boolean | null;
  shipmentErrorMessages?: string[] | null;
}> {
  const printFast = localStorage.getItem("printFast") === "true";

  switch (document) {
    case PrintableDocument.LABEL:
      return await client.printLabels(
        {
          ...batch,
          workstationId,
          printerId,
          printerOptions,
          ...getBulkOneClickPrintPickListInput(
            bulkOneClickPrintPickListPosition,
          ),
          templateId,
          printFast,
          initiationTime: Date.now(),
        },
        { signal },
      );
    case PrintableDocument.PACKING_SLIP:
      return await client.printPackingSlip(
        {
          ...batch,
          workstationId,
          printerId,
          printerOptions,
          templateId,
          printFast,
          initiationTime: Date.now(),
        },
        { signal },
      );
    case PrintableDocument.PICK_LIST:
      return await client.printPickList(
        { ...batch, workstationId, printerId, printerOptions },
        { signal },
      );
    case PrintableDocument.ORDER_SUMMARY:
      return await client.printOrderSummaryReport(
        { ...batch, printerId, printerOptions },
        { signal },
      );
    case PrintableDocument.COMMERCIAL_INVOICE:
      return await client.printCommercialInvoices(
        { ...batch, workstationId, printerId, printerOptions },
        { signal },
      );
    case PrintableDocument.PERSONALIZED_SLIP:
      return await client.printPersonalizedSlip(
        { ...batch, printerId, printerOptions },
        { signal },
      );
    case PrintableDocument.RETURN_LABEL:
      return await client.printReturnLabels(
        { ...batch, workstationId, printerId, printerOptions },
        { signal },
      );
    case PrintableDocument.LOCAL_PICK_UP_LABEL:
      return await client.printLocalPickUpLabels(
        { ...batch, printerId, printerOptions },
        { signal },
      );
    case PrintableDocument.BILL_OF_LADING:
      return await client.printBillOfLading(
        { ...batch, printerId, printerOptions },
        { signal },
      );
    case PrintableDocument.PARCEL_PACKING_SLIP: {
      if (!("fulfillmentGroupIds" in batch)) {
        throw new Error(
          "Package packing slips require fulfillment group IDs, not a batch ID",
        );
      }
      return await client.printParcelPackingSlips(
        {
          fulfillmentGroupIds: batch.fulfillmentGroupIds,
          workstationId,
          printerId,
          printerOptions,
          templateId,
        },
        { signal },
      );
    }
    default:
      assertNever(document);
  }
}
